import { useState } from "react";
import axios from "axios";
import {
    HeartHandshake,
    DollarSign,
    Send,
    Loader2,
    CheckCircle2,
    MessageSquareText,
    Search,
    Coins,
    Inbox,
    Hourglass,
    Trophy,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    REV_CASH_AMOUNT,
    REV_CASH_TIERS,
    SMS_HREF,
    PHONE_DISPLAY,
} from "@/lib/site";
import { API } from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import { track } from "@/lib/track";
import Breadcrumb from "@/components/Breadcrumb";
import { EditableText } from "@/lib/editMode";

const initial = {
    referrer_name: "",
    referrer_phone: "",
    referrer_email: "",
    friend_name: "",
    friend_phone: "",
    note: "",
};

const STATUS_META = {
    pending: { t: "Waiting", c: "bg-zinc-700 text-zinc-100" },
    quote_requested: {
        t: "In Process",
        c: "bg-amber-600/30 text-amber-300 border border-amber-600/50",
    },
    completed: {
        t: "Completed",
        c: "bg-blue-600/30 text-blue-300 border border-blue-600/50",
    },
    paid: {
        t: "Paid",
        c: "bg-green-600/30 text-green-300 border border-green-600/50",
    },
};

function StatusPill({ status }) {
    const meta = STATUS_META[status] || STATUS_META.pending;
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest ${meta.c}`}
        >
            {meta.t}
        </span>
    );
}

function maskPhone(p) {
    if (!p) return "";
    const digits = p.replace(/\D/g, "");
    if (digits.length < 4) return p;
    return `••• •••-${digits.slice(-4)}`;
}

function formatDate(s) {
    try {
        return new Date(s).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return s;
    }
}

export default function Refer() {
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Rev Cash lookup state
    const [lookupPhone, setLookupPhone] = useState("");
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupData, setLookupData] = useState(null);
    const [lookupSearched, setLookupSearched] = useState(false);

    useSEO({
        title: `Refer & Earn — Get $${REV_CASH_AMOUNT} Rev Cash + Tier Bonuses`,
        description: `Refer a friend to Limit Break Performance — earn $${REV_CASH_AMOUNT} per completed job, plus tier bonuses ($100 at 5 referrals, $250 at 10). Check your balance right on this page.`,
    });

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.referrer_name || !form.referrer_phone || !form.friend_phone) {
            toast.error("Need your name, your phone, and your friend's phone.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/referrals`, form);
            setSubmitted(true);
            setForm(initial);
            toast.success("Referral logged. We'll text you when they reach out.");
            track("generate_lead", { source: "referral" });
        } catch (err) {
            console.error(err);
            toast.error("Couldn't log referral. Try texting us directly.");
        } finally {
            setSubmitting(false);
        }
    };

    const onLookup = async (e) => {
        e.preventDefault();
        if (!lookupPhone.trim()) {
            toast.error("Drop your phone number to look up your account.");
            return;
        }
        setLookupLoading(true);
        try {
            const res = await axios.post(`${API}/my-rev-cash`, {
                phone: lookupPhone,
            });
            setLookupData(res.data);
            setLookupSearched(true);
        } catch (err) {
            console.error(err);
            toast.error("Couldn't look that up. Try again or text us.");
        } finally {
            setLookupLoading(false);
        }
    };

    return (
        <div data-testid="refer-page">
            <Breadcrumb section="Refer & Earn" />

            {/* HERO */}
            <section className="relative border-b border-white/10 bg-black overflow-hidden">
                <div className="absolute inset-0 lbp-grid-overlay opacity-30" />
                <div className="relative lbp-container pt-8 pb-14 sm:pb-16">
                    <EditableText
                        path="refer.hero.eyebrow"
                        defaultValue="Rev Cash Program"
                        as="p"
                        className="text-xs uppercase tracking-widest text-red-500 font-semibold"
                    />
                    <h1
                        className="mt-2 font-display font-black uppercase tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl"
                        data-testid="refer-hero-title"
                    >
                        <EditableText path="refer.hero.title_line1" defaultValue="Refer A Friend." as="span" />
                        <br />
                        <span className="text-red-500">
                            <EditableText path="refer.hero.title_line2" defaultValue={`Get $${REV_CASH_AMOUNT} Rev Cash.`} as="span" />
                        </span>
                    </h1>
                    <EditableText
                        path="refer.hero.subtitle"
                        defaultValue={`Send your buddy our way. When they get a job done with Limit Break Performance, you earn $${REV_CASH_AMOUNT} in Rev Cash — credit toward your next service. The more friends you send, the more you bank.`}
                        multiline
                        as="p"
                        className="mt-5 max-w-2xl text-zinc-300 text-base sm:text-lg leading-relaxed"
                    />
                </div>
            </section>

            {/* TIER BONUSES */}
            <section className="bg-zinc-950 border-b border-white/10">
                <div className="lbp-container py-12 sm:py-16">
                    <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                        <div>
                            <EditableText
                                path="refer.tiers.eyebrow"
                                defaultValue="Stack Your Bonuses"
                                as="p"
                                className="text-xs uppercase tracking-widest text-red-500 font-semibold"
                            />
                            <EditableText
                                path="refer.tiers.title"
                                defaultValue="The More You Refer, The More You Stack."
                                as="h2"
                                className="mt-2 font-display font-black uppercase tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl"
                            />
                        </div>
                        <EditableText
                            path="refer.tiers.body"
                            defaultValue={`Hit a tier and a bonus drops on top of every $${REV_CASH_AMOUNT} you've already earned. No cap on referrals.`}
                            multiline
                            as="p"
                            className="text-sm text-zinc-500 max-w-md"
                        />
                    </div>

                    <div className="grid sm:grid-cols-3 gap-4">
                        {REV_CASH_TIERS.map((tier) => (
                            <div
                                key={tier.key}
                                data-testid={`refer-tier-${tier.key}`}
                                className={`rounded-sm border bg-gradient-to-br p-6 ${tier.color}`}
                            >
                                <div className="flex items-center gap-2">
                                    <Trophy
                                        className={`w-5 h-5 ${tier.accent}`}
                                    />
                                    <span
                                        className={`text-[11px] uppercase tracking-widest font-bold ${tier.accent}`}
                                    >
                                        {tier.title}
                                    </span>
                                </div>
                                <div className="mt-3 font-display font-black uppercase text-white text-3xl tracking-tighter">
                                    {tier.threshold}+ Referrals
                                </div>
                                <ul className="mt-4 space-y-2">
                                    {tier.perks.map((perk) => (
                                        <li
                                            key={perk}
                                            className="flex items-start gap-2 text-sm text-zinc-300 leading-relaxed"
                                        >
                                            <CheckCircle2
                                                className={`w-4 h-4 ${tier.accent} mt-0.5 flex-shrink-0`}
                                            />
                                            <span>{perk}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="bg-black border-b border-white/10">
                <div className="lbp-container py-12 sm:py-16">
                    <EditableText
                        path="refer.how.eyebrow"
                        defaultValue="How it works"
                        as="p"
                        className="text-xs uppercase tracking-widest text-red-500 font-semibold"
                    />
                    <EditableText
                        path="refer.how.title"
                        defaultValue="Three Steps. That's It."
                        as="h2"
                        className="mt-2 font-display font-black uppercase tracking-tight text-white text-2xl sm:text-3xl mb-8"
                    />
                    <div className="grid sm:grid-cols-3 gap-4">
                        {[
                            { icon: Send, t: "1. Send Their Info", d: "Drop your friend's number in the form below — takes 30 seconds." },
                            { icon: HeartHandshake, t: "2. They Get Work Done", d: "When they text in for a quote, our system flags you as the referrer automatically." },
                            { icon: DollarSign, t: `3. You Earn $${REV_CASH_AMOUNT}`, d: "Once their job's complete, $" + REV_CASH_AMOUNT + " in Rev Cash hits your account. Hit a tier, stack a bonus." },
                        ].map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <div
                                    key={i}
                                    className="rounded-sm border border-white/10 bg-zinc-900 p-6"
                                >
                                    <Icon className="w-6 h-6 text-red-500" />
                                    <EditableText
                                        path={`refer.how.steps.${i}.t`}
                                        defaultValue={s.t}
                                        as="h3"
                                        className="mt-4 font-display font-bold uppercase tracking-wide text-white text-base"
                                    />
                                    <EditableText
                                        path={`refer.how.steps.${i}.d`}
                                        defaultValue={s.d}
                                        multiline
                                        as="p"
                                        className="mt-2 text-sm text-zinc-400 leading-relaxed"
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SUBMIT REFERRAL */}
            <section className="bg-zinc-950 border-b border-white/10">
                <div className="lbp-container py-14 sm:py-20 max-w-2xl">
                    {submitted ? (
                        <div
                            data-testid="refer-success-state"
                            className="rounded-sm border border-green-500/30 bg-green-500/5 p-8 text-center"
                        >
                            <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto" />
                            <h2 className="mt-4 font-display font-black uppercase tracking-tight text-white text-2xl">
                                Referral Locked In
                            </h2>
                            <p className="mt-3 text-zinc-400 max-w-md mx-auto">
                                Thanks. When your friend texts in, our system
                                will flag you as the referrer. Once their job's
                                done you'll get a text from us with your $
                                {REV_CASH_AMOUNT} Rev Cash credit.
                            </p>
                            <button
                                type="button"
                                onClick={() => setSubmitted(false)}
                                data-testid="refer-another-button"
                                className="mt-6 inline-flex items-center gap-2 border border-white/20 hover:border-white text-white px-6 py-3 rounded-sm font-display font-bold uppercase tracking-wide text-sm"
                            >
                                Refer Another
                            </button>
                        </div>
                    ) : (
                        <form
                            onSubmit={onSubmit}
                            data-testid="refer-form"
                            noValidate
                            className="rounded-sm border border-white/10 bg-zinc-900 p-6 sm:p-8 space-y-5"
                        >
                            <h2 className="font-display font-black uppercase tracking-tight text-white text-2xl">
                                Submit A Referral
                            </h2>

                            <div>
                                <Label className="text-xs uppercase tracking-widest text-zinc-300 font-semibold">
                                    Your Name
                                </Label>
                                <Input
                                    name="referrer_name"
                                    value={form.referrer_name}
                                    onChange={onChange}
                                    data-testid="refer-input-referrer-name"
                                    placeholder="Your full name"
                                    className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white placeholder:text-zinc-600 h-11"
                                    required
                                />
                            </div>

                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-zinc-300 font-semibold">
                                        Your Phone
                                    </Label>
                                    <Input
                                        name="referrer_phone"
                                        value={form.referrer_phone}
                                        onChange={onChange}
                                        data-testid="refer-input-referrer-phone"
                                        placeholder="(757) 555-0123"
                                        type="tel"
                                        className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white placeholder:text-zinc-600 h-11"
                                        required
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest text-zinc-300 font-semibold">
                                        Your Email (optional)
                                    </Label>
                                    <Input
                                        name="referrer_email"
                                        value={form.referrer_email}
                                        onChange={onChange}
                                        data-testid="refer-input-referrer-email"
                                        placeholder="you@email.com"
                                        type="email"
                                        className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white placeholder:text-zinc-600 h-11"
                                    />
                                </div>
                            </div>

                            <div className="border-t border-white/5 pt-5">
                                <p className="text-xs uppercase tracking-widest text-red-500 font-semibold mb-4">
                                    Your Friend's Info
                                </p>
                                <div className="grid sm:grid-cols-2 gap-5">
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-zinc-300 font-semibold">
                                            Friend's Name
                                        </Label>
                                        <Input
                                            name="friend_name"
                                            value={form.friend_name}
                                            onChange={onChange}
                                            data-testid="refer-input-friend-name"
                                            placeholder="Their name"
                                            className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white placeholder:text-zinc-600 h-11"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-widest text-zinc-300 font-semibold">
                                            Friend's Phone *
                                        </Label>
                                        <Input
                                            name="friend_phone"
                                            value={form.friend_phone}
                                            onChange={onChange}
                                            data-testid="refer-input-friend-phone"
                                            placeholder="(757) 555-0123"
                                            type="tel"
                                            className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white placeholder:text-zinc-600 h-11"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <Label className="text-xs uppercase tracking-widest text-zinc-300 font-semibold">
                                    What's Their Car? (optional)
                                </Label>
                                <Textarea
                                    name="note"
                                    value={form.note}
                                    onChange={onChange}
                                    data-testid="refer-input-note"
                                    placeholder="2018 Mustang GT — needs coilovers"
                                    className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white placeholder:text-zinc-600 min-h-[90px]"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="refer-submit-button"
                                className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-70 disabled:cursor-not-allowed text-white px-6 py-4 rounded-sm font-display font-black uppercase tracking-wide text-sm sm:text-base"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Logging…
                                    </>
                                ) : (
                                    <>
                                        <MessageSquareText className="w-4 h-4" />
                                        Send Referral
                                    </>
                                )}
                            </button>

                            <p className="text-[11px] text-zinc-500 text-center leading-relaxed">
                                By submitting, you confirm your friend is OK
                                with you sharing their number. We only use it to
                                follow up on this referral.
                            </p>
                        </form>
                    )}
                </div>
            </section>

            {/* CHECK MY REV CASH */}
            <section
                id="check"
                className="bg-black border-b border-white/10 scroll-mt-24"
            >
                <div className="lbp-container py-14 sm:py-20">
                    <div className="max-w-2xl">
                        <EditableText
                            path="refer.check.eyebrow"
                            defaultValue="Already referred someone?"
                            as="p"
                            className="text-xs uppercase tracking-widest text-red-500 font-semibold"
                        />
                        <EditableText
                            path="refer.check.title"
                            defaultValue="Check My Rev Cash"
                            as="h2"
                            className="mt-2 font-display font-black uppercase tracking-tighter text-white text-3xl sm:text-4xl"
                            data-testid="my-rev-cash-hero-title"
                        />
                        <EditableText
                            path="refer.check.body"
                            defaultValue="Drop the phone number you used when submitting a referral. We'll show you who you've referred, what's in process, what's done, and how much Rev Cash you've banked."
                            multiline
                            as="p"
                            className="mt-4 text-zinc-400 text-base leading-relaxed"
                        />

                        <form
                            onSubmit={onLookup}
                            data-testid="my-rev-cash-form"
                            noValidate
                            className="mt-6 flex flex-col sm:flex-row gap-3"
                        >
                            <div className="flex-1">
                                <Label
                                    htmlFor="lookup-phone"
                                    className="text-xs uppercase tracking-widest text-zinc-300 font-semibold"
                                >
                                    Your Phone
                                </Label>
                                <Input
                                    id="lookup-phone"
                                    value={lookupPhone}
                                    onChange={(e) =>
                                        setLookupPhone(e.target.value)
                                    }
                                    data-testid="my-rev-cash-input-phone"
                                    placeholder="(757) 555-0123"
                                    type="tel"
                                    className="mt-2 bg-zinc-950 border-zinc-800 focus-visible:ring-red-600 text-white placeholder:text-zinc-600 h-12"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={lookupLoading}
                                data-testid="my-rev-cash-submit"
                                className="sm:self-end inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-70 text-white px-6 py-3 h-12 rounded-sm font-display font-bold uppercase tracking-wide text-sm"
                            >
                                {lookupLoading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <>
                                        <Search className="w-4 h-4" />
                                        Look Up
                                    </>
                                )}
                            </button>
                        </form>
                    </div>

                    {lookupSearched && lookupData && !lookupData.found && (
                        <div className="mt-10 max-w-2xl rounded-sm border border-white/10 bg-zinc-900 p-8 text-center">
                            <h3 className="font-display font-bold uppercase tracking-tight text-white text-xl">
                                Nothing under that number yet.
                            </h3>
                            <p className="mt-3 text-zinc-400">
                                Either you haven't submitted a referral with
                                this phone, or the number doesn't match. The
                                form above takes 30 seconds — drop one in.
                            </p>
                        </div>
                    )}

                    {lookupSearched && lookupData && lookupData.found && (
                        <div className="mt-10">
                            <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">
                                Welcome back
                            </div>
                            <h3 className="mt-1 font-display font-black uppercase tracking-tight text-white text-2xl sm:text-3xl">
                                {lookupData.referrer_name}
                            </h3>

                            <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
                                <div className="rounded-sm border border-green-500/30 bg-green-500/10 p-5">
                                    <div className="flex items-center gap-2">
                                        <Coins className="w-4 h-4 text-green-400" />
                                        <span className="text-xs uppercase tracking-widest text-green-400 font-semibold">
                                            Lifetime Rev Cash
                                        </span>
                                    </div>
                                    <div
                                        data-testid="my-rev-cash-lifetime"
                                        className="mt-3 font-display font-black text-white text-3xl sm:text-4xl"
                                    >
                                        ${lookupData.total_earned}
                                    </div>
                                </div>
                                <div className="rounded-sm border border-amber-500/30 bg-amber-500/10 p-5">
                                    <div className="flex items-center gap-2">
                                        <Coins className="w-4 h-4 text-amber-400" />
                                        <span className="text-xs uppercase tracking-widest text-amber-400 font-semibold">
                                            Unpaid Balance
                                        </span>
                                    </div>
                                    <div
                                        data-testid="my-rev-cash-unpaid"
                                        className="mt-3 font-display font-black text-white text-3xl sm:text-4xl"
                                    >
                                        ${lookupData.total_unpaid}
                                    </div>
                                    <div className="mt-1 text-[10px] uppercase tracking-widest text-amber-300/80">
                                        Owed to you
                                    </div>
                                </div>
                                <div className="rounded-sm border border-white/10 bg-zinc-900 p-5">
                                    <div className="flex items-center gap-2">
                                        <Hourglass className="w-4 h-4 text-red-500" />
                                        <span className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">
                                            In Process
                                        </span>
                                    </div>
                                    <div className="mt-3 font-display font-black text-white text-3xl sm:text-4xl">
                                        {lookupData.in_progress}
                                    </div>
                                </div>
                                <div className="rounded-sm border border-white/10 bg-zinc-900 p-5">
                                    <div className="flex items-center gap-2">
                                        <Inbox className="w-4 h-4 text-red-500" />
                                        <span className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">
                                            Total Referrals
                                        </span>
                                    </div>
                                    <div className="mt-3 font-display font-black text-white text-3xl sm:text-4xl">
                                        {lookupData.total_referrals}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8">
                                <h4 className="font-display font-bold uppercase tracking-tight text-white text-lg">
                                    Your Referrals
                                </h4>
                                <div className="mt-4 grid gap-3">
                                    {lookupData.referrals.map((r) => (
                                        <div
                                            key={r.id}
                                            data-testid={`my-rev-cash-row-${r.id}`}
                                            className="rounded-sm border border-white/10 bg-zinc-900 p-5 grid sm:grid-cols-[1fr_auto] gap-4 items-center"
                                        >
                                            <div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-white font-semibold">
                                                        {r.friend_name ||
                                                            "Friend"}
                                                    </span>
                                                    <span className="text-zinc-500 text-sm">
                                                        {maskPhone(
                                                            r.friend_phone
                                                        )}
                                                    </span>
                                                    <StatusPill
                                                        status={r.status}
                                                    />
                                                </div>
                                                <div className="mt-1 text-xs text-zinc-500">
                                                    Submitted{" "}
                                                    {formatDate(r.created_at)}
                                                    {r.completed_at &&
                                                    r.status !== "pending"
                                                        ? ` · Completed ${formatDate(
                                                              r.completed_at
                                                          )}`
                                                        : ""}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-display font-black text-white text-2xl">
                                                    ${r.rev_cash}
                                                </div>
                                                {r.status === "paid" ? (
                                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-green-400 font-semibold">
                                                        <CheckCircle2 className="w-3 h-3" />{" "}
                                                        Paid out
                                                    </span>
                                                ) : r.status === "completed" ? (
                                                    <span className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
                                                        Owed to you
                                                    </span>
                                                ) : r.status ===
                                                  "quote_requested" ? (
                                                    <span className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
                                                        Pending job complete
                                                    </span>
                                                ) : (
                                                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                                                        Hasn't reached out yet
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <p className="mt-6 text-[11px] text-zinc-500">
                                Questions about your balance or want to redeem?
                                Text {PHONE_DISPLAY}.
                            </p>
                        </div>
                    )}
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="bg-zinc-950">
                <div className="lbp-container py-12 sm:py-16">
                    <div className="rounded-sm border border-red-600/30 bg-red-600/10 p-6 sm:p-8 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
                        <div>
                            <h3 className="font-display font-black uppercase tracking-tight text-white text-xl sm:text-2xl">
                                Want To Stack More?
                            </h3>
                            <p className="mt-1 text-zinc-300 text-sm sm:text-base">
                                Every friend you refer adds ${REV_CASH_AMOUNT}{" "}
                                once their job's done. Hit a tier and stack
                                bonuses on top.
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <a
                                href="#check"
                                data-testid="refer-bottom-check"
                                className="inline-flex items-center gap-2 border border-white/25 hover:border-white text-white px-5 py-3 rounded-sm font-display font-bold uppercase tracking-wide text-sm"
                            >
                                Check Balance
                            </a>
                            <a
                                href={SMS_HREF}
                                data-testid="refer-bottom-text"
                                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-3 rounded-sm font-display font-bold uppercase tracking-wide text-sm"
                            >
                                <MessageSquareText className="w-4 h-4" /> Text Us
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
